/**
 * WebGL Instance Hook for WebGLChart
 * Manages webgl-plot chart lifecycle and configuration
 */

import { useEffect, useRef, useCallback } from 'react';
import { WebglPlot, WebglLine, ColorRGBA } from 'webgl-plot';
import { DisplayData, ProcessedChartData, ZoomState, Bounds } from '../types';

const DEFAULT_LINE_COLOR = new ColorRGBA(0.082, 0.722, 0.651, 1); // #14B8A6

function hexToColorRGBA(hex: string | undefined): ColorRGBA {
  if (!hex) return DEFAULT_LINE_COLOR;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return DEFAULT_LINE_COLOR;
  const n = parseInt(m[1], 16);
  return new ColorRGBA(
    ((n >> 16) & 0xff) / 255,
    ((n >> 8) & 0xff) / 255,
    (n & 0xff) / 255,
    1,
  );
}

interface UseWebGLInstanceProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  displayData: DisplayData;
  processedData: ProcessedChartData;
  seriesName: string;
  showPoints: boolean;
  zoomState: ZoomState;
  onZoomChange: (zoom: ZoomState) => void;
  chartWidth: number;
  chartHeight: number;
  color?: string;
}

export function useWebGLInstance({
  canvasRef,
  displayData,
  processedData,
  showPoints,
  zoomState,
  onZoomChange,
  chartWidth,
  chartHeight,
  color,
}: UseWebGLInstanceProps) {
  const wglpRef = useRef<WebglPlot | null>(null);
  const lineRef = useRef<WebglLine | null>(null);
  const pointsLineRef = useRef<WebglLine | null>(null);
  const initialBoundsRef = useRef<Bounds | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Initialize WebGL plot
  useEffect(() => {
    if (!canvasRef.current || displayData.timestamps.length === 0) return;

    const canvas = canvasRef.current;
    const { timestamps, values } = displayData;
    const { yMin, yMax } = processedData;

    // Wait for canvas to have valid dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      return;
    }

    // Store initial bounds for reset
    initialBoundsRef.current = {
      xMin: timestamps[0],
      xMax: timestamps[timestamps.length - 1],
      yMin,
      yMax,
    };

    // Cleanup previous instance
    if (wglpRef.current) {
      wglpRef.current.removeAllLines();
    }

    // Create WebGL plot
    try {
      const wglp = new WebglPlot(canvas);
      wglpRef.current = wglp;

      // Create line with number of points
      const line = new WebglLine(hexToColorRGBA(color), timestamps.length);
      lineRef.current = line;

      // Normalize data to WebGL coordinate space (-1 to 1)
      const xRange = timestamps[timestamps.length - 1] - timestamps[0];
      const yRange = yMax - yMin || 1;
      const yPadding = yRange * 0.05;
      const yMinPadded = yMin - yPadding;
      const yMaxPadded = yMax + yPadding;
      const yRangePadded = yMaxPadded - yMinPadded;

      for (let i = 0; i < timestamps.length; i++) {
        // Normalize X: timestamp → [-1, 1]
        const normalizedX = ((timestamps[i] - timestamps[0]) / xRange) * 2 - 1;
        line.setX(i, normalizedX);

        // Normalize Y: value → [-1, 1] with padding
        const v = values[i];
        if (v !== null && Number.isFinite(v)) {
          const normalizedY = ((v - yMinPadded) / yRangePadded) * 2 - 1;
          line.setY(i, normalizedY);
        } else {
          // For null values, use NaN to create gap
          line.setY(i, NaN);
        }
      }

      wglp.addLine(line);

      // Apply initial zoom state
      wglp.gScaleX = zoomState.scaleX;
      wglp.gScaleY = zoomState.scaleY;
      wglp.gOffsetX = zoomState.offsetX;
      wglp.gOffsetY = zoomState.offsetY;

      wglp.update();
    } catch {
      // WebGL initialization failed - silently handle
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (wglpRef.current) {
        wglpRef.current.removeAllLines();
      }
      wglpRef.current = null;
      lineRef.current = null;
      pointsLineRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayData, processedData.yMin, processedData.yMax, canvasRef, chartWidth, chartHeight, color]);

  // Apply zoom state changes
  useEffect(() => {
    if (!wglpRef.current) return;

    wglpRef.current.gScaleX = zoomState.scaleX;
    wglpRef.current.gScaleY = zoomState.scaleY;
    wglpRef.current.gOffsetX = zoomState.offsetX;
    wglpRef.current.gOffsetY = zoomState.offsetY;
    wglpRef.current.update();
  }, [zoomState]);

  // Handle points visibility
  useEffect(() => {
    if (!wglpRef.current || !lineRef.current) return;

    // For now, webgl-plot doesn't have native points support
    // Points would need to be implemented as separate scatter plot
    // This is a placeholder for future enhancement
  }, [showPoints]);

  // Reset zoom function
  const handleResetZoom = useCallback(() => {
    onZoomChange({
      scaleX: 1,
      scaleY: 1,
      offsetX: 0,
      offsetY: 0,
      xMin: initialBoundsRef.current?.xMin ?? 0,
      xMax: initialBoundsRef.current?.xMax ?? 1,
    });
  }, [onZoomChange]);

  return {
    wglpRef,
    lineRef,
    handleResetZoom,
    initialBounds: initialBoundsRef,
  };
}
